import { CurrencyPipe, DatePipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit, viewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonActivity, CourierType, ENVIRONMENT, Environment, ErrorCode, Lead, PageActivity, Pricing, Specification } from '@domains';
import { PageFooterComponent } from '@donor/components/page-footer/page-footer.component';
import { StripeComponent } from '@payment';
import {
  ConfirmDialogComponent,
  DesignService,
  DiscountInfoDialogComponent,
  DonationEstimateComponent,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
  ResponsiveService,
} from '@rspl-ui';
import { LaddaModule } from 'angular2-ladda';
import * as moment from 'moment';
import { CountdownComponent, CountdownEvent } from 'ngx-countdown';
import { NgxCurrencyDirective } from 'ngx-currency';
import { of, take } from 'rxjs';

import { AppService } from '../../../../app.service';
import { CreateProgressComponent } from '../../../../components/create-progress/create-progress.component';
import { InfoDialogComponent } from '../../../../components/info-dialog/info-dialog.component';
import { PageHeaderComponent } from '../../../../components/page-header/page-header.component';
import { OptimizelyService } from '../../../../optimizely.service';
import { FlowService } from '../../donor-flow.service';
import { BaseFlowStepComponent } from '../base-flow-step.component';

export enum TipType {
  NO_TIP = 'no_tip',
  FIVE = 'five',
  TEN = 'ten',
  FIFTEEN = 'fifteen',
  CUSTOM = 'custom',
}

@Component({
  selector: 'app-flow-payment',
  templateUrl: './flow-payment.component.html',
  styleUrls: ['./flow-payment.component.scss'],
  imports: [
    NgTemplateOutlet,
    PageHeaderComponent,
    PageFooterComponent,
    FxLayoutDirective,
    FxLayoutAlignDirective,
    MatProgressSpinner,
    NgStyle,
    FxLayoutGapDirective,
    FxFlexDirective,
    CountdownComponent,
    NgClass,
    RouterLink,
    DonationEstimateComponent,
    LaddaModule,
    MatIcon,
    CreateProgressComponent,
    FormsModule,
    StripeComponent,
    CurrencyPipe,
    DatePipe,
    MatInput,
    ReactiveFormsModule,
    NgxCurrencyDirective,
  ],
  providers: [],
})
export class FlowPaymentComponent extends BaseFlowStepComponent implements OnInit {
  showQuoteDetails = false;

  readonly estimate = viewChild(DonationEstimateComponent);
  readonly payment = viewChild(StripeComponent);

  cardOnFile = false;
  expired = false;
  userTerms = false;
  isLoaded = false;
  error?: string;
  sponsoredAmount = 0;
  sponsorshipPercent = 0;
  isCalculatingPrice = false;
  leftTime: number;
  courierTypes = CourierType;
  isLoading = true;
  paymentSetupInitialized = false;
  gratuity = 0;
  customGratuity = 0;
  tipTypes = TipType;
  tip?: TipType = TipType.NO_TIP;
  tipFormControl: FormControl<number>;
  showTipError = false;

  constructor(
    override flowService: FlowService,
    override appService: AppService,
    override router: Router,
    override route: ActivatedRoute,
    override designService: DesignService,
    override responsiveService: ResponsiveService,
    protected dialog: MatDialog,
    protected override optimizelyService: OptimizelyService,
    @Inject(ENVIRONMENT) protected override environment: Environment,
  ) {
    super(flowService, appService, router, route, designService, responsiveService, optimizelyService, environment);

    if (this.route.snapshot.url.find((x) => x.path === 'card-on-file')) {
      this.cardOnFile = true;
    }

    this.onSaveSuccess = () => {
      this.isSubmitting = true;
      if (this.showCOFform || this.gratuity > 0) {
        this.payment().onPayment();
      } else {
        this.paymentSuccess();
      }
      return false;
    };
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.tip === TipType.CUSTOM) this.customGratuity = this.gratuity;
    this.flowService
      .updateLead({ gratuity: 0, quote_check: true })
      .pipe(take(1))
      .subscribe(() => {
        this.isLoading = false;
        if (this.lead?.discount?.valid) {
          this.sponsoredAmount = this.lead.quote?.discount_amount || 0;
          this.sponsorshipPercent = Math.round((this.lead.quote?.discount_amount / this.lead.quote?.total_amount) * 100);
          this.isCalculatingPrice = false;
        } else {
          this.isCalculatingPrice = false;
        }

        if (this.showCOFform || this.gratuity > 0 || this.lead.total - this.lead.totalSponsoredAmount > 0) {
          this.createPaymentSetup();
        }

        if (this.lead.isUberPickup) {
          const diff = moment().diff(this.lead.quote?.expires_at, 'seconds');
          if (diff < 0) {
            this.leftTime = Math.abs(diff);
          } else {
            this.leftTime = 0;
          }
        }
      });
  }

  createPaymentSetup(): void {
    this.isLoaded = false;
    this.flowService.createPaymentSetup(true).subscribe({
      next: () => {
        this.paymentSetupInitialized = true;
      },
      error: (error) => {
        if (error?.error?.code === ErrorCode.lead_expired_error) {
          this.expired = true;
        } else if (error?.error?.code === ErrorCode.payment_setup_completed) {
          this.router.navigate(['/', 'created', this.flowService.lead.id]);
        } else {
          this.error = 'Something went wrong, please contact support';
        }
      },
    });
  }

  onSpecificationChange() {
    this.isCalculatingPrice = true;
    this.flowService
      .updateLead(this.lead)
      .pipe(take(1))
      .subscribe(() => {
        if (this.lead?.discount?.valid) {
          this.sponsoredAmount = this.lead.quote?.discount_amount || 0;
          this.sponsorshipPercent = Math.round((this.lead.quote?.discount_amount / this.lead.quote?.total_amount) * 100);
          this.isCalculatingPrice = false;
        } else {
          this.isCalculatingPrice = false;
        }
      });
  }

  static override isCompleted() {
    return false;
  }

  override isValid(): boolean {
    if ((this.estimate() && new Specification(this.estimate()?.specification).isEmpty()) || this.lead?.specification?.isEmpty()) {
      this.dialog.open(InfoDialogComponent, {
        data: {
          title: 'No Items',
          message: 'You need to select at least one item to donate before proceeding to the next step',
          button: 'I understand.',
          icon: 'warning',
        },
      });
      return false;
    }
    if (!this.userTerms) {
      if (this.design4) {
        this.scrollToCheckbox();
      }
      this.appService.createLeadActivity(this.lead?.id, ButtonActivity.PAYMENT_SETUP_SUBMIT_ERROR).subscribe();
      return false;
    }
    return true;
  }

  override getFromValue(): Partial<Lead> {
    return {
      specification: new Specification(this.estimate()?.specification || this.lead.specification),
      meta: {
        ...this.lead?.meta,
      },
    };
  }

  override showError(): void {
    window.scrollTo(0, document.body.scrollHeight);
  }

  override saveButtonActivity(): ButtonActivity {
    return ButtonActivity.PAYMENT_SETUP_SUBMIT;
  }

  override pageVisitActivity(): PageActivity {
    return PageActivity.PAYMENT_SETUP_PAGE;
  }

  get donationPrice(): number {
    return this.lead?.specification && this.pricing ? Pricing.getTotalPrice(this.lead.specification, this.pricing) : 0;
  }

  onCheckboxChange() {
    this.userTerms = !this.userTerms;
    this.appService
      .createLeadActivity(this.lead?.id, ButtonActivity.PAYMENT_CHECKBOX, this.userTerms ? 'checked' : 'unchecked')
      .subscribe();
  }

  scrollToCheckbox(): void {
    const parent = document.querySelector('.checkboxes');
    const checkbox: any = document.querySelector('.checkboxes .checkbox');
    if (parent) {
      const headerOffset = 90;
      const elementPosition = parent.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;

      setTimeout(() => {
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
        checkbox.focus();
      });
    }
  }

  cardReady(isReady: boolean) {
    this.isLoaded = isReady;
  }

  disabledButtonsClicked() {
    if (!this.isValid()) {
      this.showError();
    }
  }

  onPaymentSetupSuccess(activity: ButtonActivity) {
    this.appService.createLeadActivity(this.lead?.id, activity).subscribe();
  }

  protected onPaymentError(err?: string) {
    this.appService.createLeadActivity(this.lead?.id, ButtonActivity.PAYMENT_SETUP_SUBMIT_ERROR).subscribe();
    this.isSubmitting = false;
    this.error = err;
  }

  paymentSuccess() {
    this.appService.createLeadActivity(this.lead?.id, ButtonActivity.PAYMENT_SETUP_SUBMIT).subscribe(() => {
      const leadId = this.flowService.lead.id;
      this.flowService.reset();
      this.router.navigate(['/', 'created', leadId]);
    });
  }

  showDiscountInfoDialog() {
    this.dialog.open(DiscountInfoDialogComponent, {
      panelClass: 'discount-info-dialog',
      data: {
        charityName: this.lead.charity?.name,
        lead: this.lead,
      },
    });
  }

  formatLeftTime(data: { date: number; formatStr: string; timezone?: string }) {
    const time = data.date / 1000;
    const hours = Math.floor(time / 60 / 60);
    const minutes = Math.floor(time / 60) - hours * 60;
    const seconds = time - hours * 60 * 60 - minutes * 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  quoteExpired(event: CountdownEvent): void {
    if (event.action === 'done') {
      this.dialog
        .open(InfoDialogComponent, {
          disableClose: true,
          data: {
            title: 'Your reservation has expired',
            message:
              'Your reservation has timed out and needs to be refreshed. Our availability changes regularly, but don’t worry, most reservations remain unchanged.',
            button: 'Refresh Reservation',
            icon: 'info',
          },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          window.location.reload();
        });
    }
  }

  get showCOFform(): boolean {
    return (
      (this.lead?.isPartnerPickup && (this.lead?.total > this.lead?.totalSponsoredAmount || this.lead?.pricing?.bookingFee > 0)) ||
      (this.lead?.isUberPickup && this.lead?.total > this.lead?.totalSponsoredAmount)
    );
  }

  _24hTo12h(hour: number): number {
    return hour > 12 ? hour - 12 : hour;
  }

  public confirmPayment() {
    if (!this.isValid()) {
      this.onError();
      return;
    }
    this.isSubmitting = true;
    this.saveLead();
  }

  toggleTip(tip: TipType) {
    if (this.tip !== tip) {
      this.tip = tip;
      this.setGratuity();
    }
  }

  setGratuity() {
    let gratuity: number | undefined | null = 0;
    switch (this.tip) {
      case TipType.NO_TIP:
        gratuity = 0;
        break;
      case TipType.FIVE:
        gratuity = 5;
        break;
      case TipType.TEN:
        gratuity = 10;
        break;
      case TipType.FIFTEEN:
        gratuity = 15;
        break;
      case TipType.CUSTOM:
        gratuity = this.customGratuity;
        break;
    }
    if (this.tip !== TipType.CUSTOM) {
      this.customGratuity = 0;
    }
    this.updateTip(gratuity);
  }

  toggleCustom(event: MouseEvent, inputElement: HTMLInputElement) {
    if (this.tip !== TipType.CUSTOM) {
      if (this.customGratuity === 0) this.customGratuity = this.gratuity;
      this.tip = TipType.CUSTOM;
      this.gratuity = this.customGratuity;
      inputElement.select();
      return;
    }
    if (event.target !== inputElement) {
      this.toggleTip(TipType.CUSTOM);
    }
  }

  updateTip(gratuity: number | string | null | undefined) {
    if (this.gratuity !== Math.round(Number(gratuity) * 100) / 100) {
      this.isLoaded = false;
      this.paymentSetupInitialized = false;
      if (this.payment()) this.payment().isPaymentElementLoaded = false;
      this.gratuity = Math.round(Number(gratuity) * 100) / 100;
      if (this.tip === TipType.CUSTOM) this.customGratuity = this.gratuity;
      (this.gratuity !== this.flowService.lead.gratuity ? this.flowService.updateLead({ gratuity: this.gratuity }) : of({}))
        .pipe(take(1))
        .subscribe(() => {
          if (this.gratuity > 0 || this.lead.total - this.lead.totalSponsoredAmount > 0) {
            this.createPaymentSetup();
          }
        });
    }
  }

  setCustomGratuity() {
    this.gratuity = this.customGratuity;
  }

  setCustomTip(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.showTipError = true;

    if (this.customGratuity >= 0.5 && this.customGratuity <= 20) {
      this.setGratuity();
    }
  }

  editSpecification() {
    this.appService.createLeadActivity(this.lead?.id, ButtonActivity.EDIT_ITEMS_FROM_PAYMENT, undefined).pipe(take(1)).subscribe();
    const confirm = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      maxWidth: '100vw',
      data: {
        title: 'Edit Items?',
        message:
          'Editing your items will send you back to the Add Items step. Rest assured, your previously entered information will be saved',
        yes: 'Proceed to Edit',
        color: 'primary',
        hideNo: true,
        showDismissIcon: true,
      },
    });
    confirm.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.router.navigate(['/', 'spec', this.flowType, this.flowId]);
      }
    });
  }

  editTime() {
    const confirm = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      maxWidth: '100vw',
      data: {
        title: 'Edit pickup Date/Time?',
        message:
          'Editing the pickup date/time will send you back to the Schedule step. Rest assured, your previously entered information will be saved',
        yes: 'Proceed to Edit',
        color: 'primary',
        hideNo: true,
        showDismissIcon: true,
      },
    });
    confirm.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.router.navigate(['/', 'place-time', this.flowType, this.flowId]);
      }
    });
  }

  editAddress() {
    const confirm = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      maxWidth: '100vw',
      data: {
        title: 'Edit pickup Address?',
        message:
          'Editing the pickup address will send you back to the Schedule step. Rest assured, your previously entered information will be saved.',
        yes: 'Proceed to Edit',
        color: 'primary',
        hideNo: true,
        showDismissIcon: true,
      },
    });
    confirm.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.router.navigate(['/', 'place-time', this.flowType, this.flowId]);
      }
    });
  }
}
